import React, { useCallback, useMemo } from "react";
import { Spin, Empty } from "antd";
import { usePosProductsQuery } from "../../../Services";
import { useCart } from "../../../Context";
import { PosProduct } from "../../../Types";
import { TabView, getProductImage } from "./shortcut-panel";

interface PosShortcutPanelProps {
  onSelectProduct?: (product: any) => void;
}

export const PosShortcutPanel: React.FC<PosShortcutPanelProps> = ({
  onSelectProduct,
}) => {
  const { isWholeSale, billingLayout } = useCart();
  const { data: posProducts = [], isLoading } = usePosProductsQuery();

  const handleAdd = useCallback(
    (product: PosProduct) => {
      const productAsSelected: any = {
        ...product,
        image_paths: getProductImage(product.image_paths) || "",
      };
      onSelectProduct?.(productAsSelected);
    },
    [onSelectProduct],
  );

  const categories = useMemo(() => {
    const map = new Map<
      string,
      { id: string; name: string; products: PosProduct[] }
    >();
    posProducts.forEach((p) => {
      const catId = p.category_id || "uncategorized";
      const catName = p.category_name || "Uncategorized";
      if (!map.has(catId)) {
        map.set(catId, { id: catId, name: catName, products: [] });
      }
      map.get(catId)!.products.push(p);
    });
    return Array.from(map.values());
  }, [posProducts]);

  const isCompact = billingLayout === "compact";

  const gridClass = isCompact
    ? "flex flex-col gap-1.5"
    : "grid gap-2 grid-cols-3 sm:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-24">
        <Spin size="small" />
        <span className="ml-2 text-xs text-gray-400">Loading shortcuts...</span>
      </div>
    );
  }

  if (posProducts.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <span className="text-xs text-gray-400">
            No POS shortcuts yet. Mark products as <strong>Add to POS</strong>{" "}
            from the Products page.
          </span>
        }
        className="py-6"
      />
    );
  }

  return (
    <div className="overflow-y-auto">
      <TabView
        categories={categories}
        isWholeSale={isWholeSale}
        isCompact={isCompact}
        gridClass={gridClass}
        onAdd={handleAdd}
      />
    </div>
  );
};
